import {TimelineLite, Linear, Power2} from "gsap";
import {DDay} from "../DDay";
import {Layers} from "./DDayLayers";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BlitzKriegReelController} from "../reels/BlitzKriegReelController";
import {DDayGameAssets} from "../DDayGameAssets";
import {DDaySymbol} from "../symbol/DDaySymbol";
import {IFeatureData} from "../server/data/IBlitzKriegData";
import {DDayGameConfig} from "../DDayGameConfig";
import {BlitzKriegSound} from "../sound/BlitzKriegSound";


export class DDayIntroSplashView extends PIXI.Container {
    private _layer: StageLayer;
    private _upperLayer: StageLayer;
    private _gameText1: PIXI.Sprite;
    private _gameText2: PIXI.Sprite;
    private _gameText3: PIXI.Sprite;

    constructor() {
        super();
        this._layer = NolimitApplication.getLayerByName(Layers.FEATURE_NON_ACTIVE)!;
        this._upperLayer = NolimitApplication.getLayerByName(Layers.FEATURE_ACTIVE)!;
    }

    public animateGameTextOnScreen(delay: number): TimelineLite {
        let graphic: PIXI.Graphics = new PIXI.Graphics();
        graphic.beginFill(0x000000, 1);
        graphic.drawRect(-NolimitApplication.screenBounds.width, 300, NolimitApplication.screenBounds.width * 3, 120);
        graphic.alpha = 0;
        graphic.endFill();
        this._gameText1 = new PIXI.Sprite(DDay.assetLoader.getTextures("logo/d")[0]);
        this._gameText2 = new PIXI.Sprite(DDay.assetLoader.getTextures("logo/day")[0]);
        this._gameText3 = new PIXI.Sprite(DDay.assetLoader.getTextures("logo/dash")[0]);
        this._gameText3.alpha = 0;
        this._gameText1.anchor.set(0.5, 0.5);
        this._gameText2.anchor.set(0.5, 0.5);
        this._gameText3.anchor.set(0.5, 0.5);
        this._gameText1.scale.set(1.3, 1.3);
        this._gameText2.scale.set(1.3, 1.3);
        this._gameText3.scale.set(1.3, 1.3);
        this._gameText1.position.set(360 - (NolimitApplication.screenBounds.width / 2) - 120, 360);
        this._gameText2.position.set(360 + (NolimitApplication.screenBounds.width / 2) + 120, 360);
        this._gameText3.position.set(340, 360);
        this._upperLayer.addChild(graphic, this._gameText3, this._gameText2, this._gameText1);
        let tl: TimelineLite = new TimelineLite();
        tl.to(this, {
            duration: delay
        }, "delay");
        tl.to(this._gameText1, {
            x: 360 - 80,
            duration: 0.5,
            ease: Power2.easeIn
        }, "start");
        tl.to(graphic, {
            alpha: 0.5,
            duration: 0.3,
        }, "start");
        tl.to(this._gameText2, {
            x: 360 + 100,
            duration: 0.5,
            ease: Power2.easeIn
        }, "start");
        tl.to(this, {
            duration: 1
        }, "pause");
        tl.to(this._gameText3, {
            alpha: 1,
            duration: 0.2
        }, "pause");
        tl.to(this._gameText3, {
            alpha: 0,
            duration: 0.2
        }, "back");
        tl.to(this._gameText1, {
            x: 360 - (NolimitApplication.screenBounds.width / 2) - 100,
            duration: 0.3,
            ease: Power2.easeOut
        }, "back");
        tl.to(graphic, {
            alpha: 0,
            duration: 0.2
        }, "back")
        tl.to(this._gameText2, {
            x: 360 + (NolimitApplication.screenBounds.width / 2) + 150,
            duration: 0.3,
            ease: Power2.easeOut
        }, "back");
        tl.add(() => {
            this._upperLayer.removeChild(graphic, this._gameText1, this._gameText2);
        }, "remove");
        tl.addLabel("delay");
        tl.addLabel("start");
        tl.addLabel("pause");
        tl.addLabel("back");
        tl.addLabel("remove");
        return tl;
    }

    public async animateBackground(): Promise<any> {
        DDay.keypadPlugin.toggleVisibility(false);
        let tl: TimelineLite = new TimelineLite();
        let bomberPlane: PIXI.Sprite[] = [];
        let lines: PIXI.Graphics[] = [];
        for (let i: number = 0; i < 3; i++) {
            let bomber: PIXI.Sprite = new PIXI.Sprite(DDay.assetLoader.getTextures("featureSymbols/bomber")[0]);
            bomber.anchor.set(0.5, 0.5);
            bomber.alpha = 0;
            bomber.scale.set(0.8, 0.8);
            bomber.position.x = (i * 144 * 1.5) + 130;
            bomber.position.y = 0;
            bomberPlane.push(bomber);
            let line: PIXI.Graphics = new PIXI.Graphics();
            line.beginFill(0x87936E, 1);
            line.drawRect(bomber.position.x, -100, 3, 300);
            line.endFill();
            lines.push(line);
            line.alpha = 0;
            this._layer.addChild(line, bomber);
        }
        tl.add(() => {
            BlitzKriegSound.playTrack({name: BlitzKriegSound.REEL_OPEN});
        }, "beforeStart");
        tl.add(DDay.backgroundView.playBackgroundFadeAnimation({alpha: 0.2}, {
            alpha: 0.8,
            duration: 1,
            ease: Linear.easeInOut
        }), "beforeStart");
        tl.add(DDay.backgroundView.playBackgroundScaleAnimation({x: 2, y: 2}, {
            x: 1,
            y: 1,
            duration: 1,
            ease: Power2.easeOut
        }), "beforeStart");
        this.setBrickView();
        DDay.reels.forEach((reel: BlitzKriegReelController) => {
            tl.add(reel.reelView.playIntroAnimation(), "start");
        });
        tl.add(DDay.reelBackground.playIntroAnimation(), "start");
        tl.add(() => {
            DDay.reelBackground.setXWaysCount(720);
        }, "tankAnimation");
        bomberPlane.forEach((bomber: PIXI.Sprite) => {
            tl.fromTo(bomber, {
                y: -300,
                alpha: 1
            }, {
                y: 0,
                duration: 1,
                ease: Linear.easeInOut
            }, "beforeStart");
            tl.fromTo(bomber, {
                y: 0,
            }, {y: 1000, duration: 1, ease: Linear.easeInOut}, "start");

            tl.to(bomber, {
                alpha: 0,
                duration: 0.2,
                ease: Linear.easeInOut
            }, "beforeEnd");
        });
        lines.forEach((line) => {
            tl.fromTo(line, {
                y: -500,
                alpha: 1
            }, {
                y: -300,
                duration: 1,
                ease: Linear.easeInOut
            }, "beforeStart");
            tl.fromTo(line, {
                y: -300,
            }, {y: 700, duration: 1, ease: Linear.easeInOut}, "start");

            tl.to(line, {
                alpha: 0,
                duration: 0.2,
                ease: Linear.easeInOut
            }, "beforeEnd");
        });
        let featureData: IFeatureData = {rows: [0, 1, 2, 3, 4,], Index: 1};
        tl.add([this.symbolExplosion(), DDay.backgroundView.fadeOutBackground(1, 0.2), DDay.introSplash.animateGameTextOnScreen(0), DDay.reelBackground.enableRadioAndWaysFrame(), DDay.reelBackground.playRadioIdleAnimation(false)], "tankAnimation");

        tl.add([() => {
            DDay.reelBackground.enableLogo(true);
            bomberPlane.forEach((bomber: PIXI.Sprite) => {
                bomber.parent.removeChild(bomber);
            });
            lines.forEach((line: PIXI.Graphics) => {
                line.parent.removeChild(line);
            });
            // Enabling Spin Button

            DDay.keypadPlugin.toggleVisibility(true);
            this.remove();
        }], "end");

        tl.addLabel("beforeStart");
        tl.addLabel("start");
        tl.addLabel("beforeEnd");
        tl.addLabel("tankAnimation");
        tl.addLabel("end");
        return tl;
    }

    public setBrickView(): void {
        DDay.reels.forEach((reel: BlitzKriegReelController, reelId: number) => {
            for (let symId: number = 0; symId < DDayGameConfig.REELS_SYM_COUNT[reelId]; symId++) {
                let symbol = reel.getSymbol(symId);
                let brickImage: PIXI.Sprite = new PIXI.Sprite(DDay.assetLoader.getTextures(DDayGameAssets.BRICK)[0]);
                brickImage.anchor.set(0.5, 0.5);
                symbol.brickImg = brickImage;
                symbol.addChild(brickImage);
            }
        });
    }

    public symbolExplosion(): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        let symbolTl: TimelineLite[] = [];
        DDay.reels.forEach((reelController: BlitzKriegReelController): void => {
            reelController.reelView.allSymbols.forEach((symbol: DDaySymbol) => {
                let animationTl: TimelineLite = new TimelineLite();
                if (symbol) {
                    animationTl.to(symbol.brickImg, {
                        alpha: 0,
                        duration: 1,
                        ease: Power2.easeOut
                    });
                    if (symbol.brickImg) {
                        animationTl.add((): void => {
                            symbol.removeChild(symbol.brickImg);
                        });
                    }
                    symbolTl.push(animationTl);
                }
            });
        });
        tl.add(symbolTl, 0);
        return tl;
    }

    public remove(): void {
        this._upperLayer.removeChildren(0);
        this._gameText1.destroy();
        this._gameText2.destroy();
        this._gameText3.destroy();
    }
}
